import { getApiClient } from './client';

export interface DatumUser {
  id: string;
  userId: string;
  data: Record<string, unknown> | null;
  createdAt: number;
  updatedAt: number;
}

export interface Datum {
  id: string;
  kind: string;
  principalId: string;
  createdBy: string;
  accessMode: 'read' | 'read_write';
  fixedData: Record<string, unknown> | null;
  createdAt: number;
  updatedAt: number;
  datumUsers: DatumUser[];
}

export interface CreateDatumRequest {
  kind: string;
  principalId: string;
  accessMode?: 'read' | 'read_write';
  fixedData?: Record<string, unknown>;
}

export async function createDatum(
  tenant: string,
  request: CreateDatumRequest
): Promise<{ signal: Datum }> {
  const client = getApiClient(tenant);
  return client.post<{ signal: Datum }>('/datum', request);
}

export interface ReminderSchema {
  title: string;
  body: string;
  schedule?: {
    startDate: string;
    endDate?: string;
    description: string;
  };
}

export async function listDatumForUser(
  tenant: string,
  kind?: string
): Promise<{ signals: Datum[] }> {
  const client = getApiClient(tenant);
  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);

  const query = params.toString();
  return client.get<{ signals: Datum[] }>(
    `/datum/user${query ? `?${query}` : ''}`
  );
}

export async function listDatumByPrincipal(
  tenant: string,
  principalId: string,
  kind?: string
): Promise<{ signals: Datum[] }> {
  const client = getApiClient(tenant);
  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);

  const query = params.toString();
  return client.get<{ signals: Datum[] }>(
    `/datum/principal/${principalId}${query ? `?${query}` : ''}`
  );
}

export async function getDatum(tenant: string, id: string): Promise<{ signal: Datum }> {
  const client = getApiClient(tenant);
  return client.get<{ signal: Datum }>(`/datum/${id}`);
}

export async function updateDatum(
  tenant: string,
  id: string,
  updates: {
    fixedData?: Record<string, unknown>;
  }
): Promise<{ signal: Datum }> {
  const client = getApiClient(tenant);
  return client.put<{ signal: Datum }>(`/datum/${id}`, updates);
}

export async function updateDatumUser(
  tenant: string,
  datumId: string,
  data: Record<string, unknown>
): Promise<{ datumUser: DatumUser }> {
  const client = getApiClient(tenant);
  return client.put<{ datumUser: DatumUser }>(`/datum/${datumId}/user`, { data });
}

export async function deleteDatum(tenant: string, id: string): Promise<void> {
  const client = getApiClient(tenant);
  await client.delete(`/datum/${id}`);
}

// Datum kind types for widget types
export const DATUM_KINDS = {
  EQUATION: 'equation',
  REFLECTION: 'reflection',
  GOAL: 'goal',
  HABIT: 'habit',
  CHECKLIST: 'checklist',
  POLL: 'poll',
  REMINDER: 'reminder',
} as const;

export type DatumKind = typeof DATUM_KINDS[keyof typeof DATUM_KINDS];
